<template>
  <div class="content-wrapper view-billing-plans pt-2">
    <div class="content">
      <b-card>
        <h4><i class="fas fa-box-open"></i> แพ็กเกจการใช้งาน</h4>
        <hr />

        <Loading v-if="isLoading" />

        <template v-else>
          <!-- สถานะปัจจุบัน -->
          <b-alert :variant="isFree ? 'secondary' : 'success'" show class="mb-4">
            <div class="d-flex justify-content-between align-items-center flex-wrap">
              <div>
                <strong>แพ็กปัจจุบัน: {{ currentPlanName }}</strong>
                <div v-if="currentEndDate" class="small mt-1">
                  ใช้งานได้ถึง {{ formatDate(currentEndDate) }}
                </div>
                <div v-else class="small mt-1">
                  สาขานี้ใช้แพ็กฟรี ได้พื้นที่ 1 GB และฟีเจอร์พื้นฐาน
                </div>
              </div>
            </div>
          </b-alert>

          <!-- สลับรายเดือน / รายปี -->
          <div class="text-center mb-4">
            <b-form-radio-group v-model="billingCycle" buttons button-variant="outline-primary">
              <b-form-radio value="MONTHLY">รายเดือน</b-form-radio>
              <b-form-radio value="YEARLY">รายปี</b-form-radio>
            </b-form-radio-group>
            <div class="small text-success mt-2">
              <i class="fas fa-gift mr-1"></i>จ่ายรายปีถูกกว่า — เท่ากับได้ใช้ฟรี 2 เดือน
            </div>
          </div>

          <!-- รายการแพ็ก -->
          <b-row>
            <b-col v-for="plan in plans" :key="plan.code" md="4" class="mb-3">
              <b-card
                class="h-100 plan-card"
                :border-variant="plan.isCurrent ? 'success' : 'default'"
              >
                <h5 class="mb-1">
                  {{ plan.name }}
                  <b-badge v-if="plan.isCurrent" variant="success">ใช้อยู่</b-badge>
                </h5>
                <div class="plan-price mb-3">
                  {{ formatMoney(priceOf(plan)) }}
                  <small class="text-muted">
                    บาท/{{ billingCycle === "YEARLY" ? "ปี" : "เดือน" }}
                  </small>
                </div>

                <ul class="plan-features list-unstyled small mb-3">
                  <li v-for="line in featureLines(plan)" :key="line">
                    <i class="fas fa-check text-success mr-1"></i>{{ line }}
                  </li>
                </ul>

                <b-button
                  block
                  :variant="plan.isCurrent ? 'outline-secondary' : 'primary'"
                  :disabled="plan.isCurrent || !canPurchase || isSubmitting"
                  @click="choose(plan)"
                >
                  {{ plan.isCurrent ? "ใช้แพ็กนี้อยู่" : "เลือกแพ็กนี้" }}
                </b-button>
              </b-card>
            </b-col>
          </b-row>

          <b-alert v-if="!canPurchase" variant="warning" show class="mt-2">
            <i class="fas fa-lock mr-1"></i>
            บัญชีของคุณดูแพ็กเกจได้ แต่ไม่มีสิทธิ์สั่งซื้อ — ติดต่อเจ้าของคลินิกหรือผู้จัดการสาขา
          </b-alert>

          <b-alert v-if="errorMessage" variant="danger" show class="mt-2">
            {{ errorMessage }}
          </b-alert>
        </template>

        <!-- ผลการจ่ายเงินหลังกลับมาจากหน้าของผู้ให้บริการ -->
        <template v-if="returnedOrder">
          <hr />
          <b-alert :variant="returnVariant" show class="mb-0">
            <div v-if="isPolling">
              <b-spinner small class="mr-2" />กำลังตรวจสอบผลการชำระเงิน…
              <div class="small mt-1">
                ระบบรอการยืนยันจากผู้ให้บริการ ปกติใช้เวลาไม่เกินหนึ่งนาที
              </div>
            </div>
            <div v-else-if="returnedOrder.status === 'COMPLETED'">
              <i class="fas fa-check-circle mr-1"></i>
              ชำระเงินสำเร็จ — เปิดใช้แพ็ก
              {{ returnedOrder.subscription && returnedOrder.subscription.planCode }}
              ถึง
              {{ formatDate(returnedOrder.subscription && returnedOrder.subscription.endDate) }}
            </div>
            <div v-else-if="returnedOrder.status === 'FAILED'">
              <i class="fas fa-times-circle mr-1"></i>
              ชำระเงินไม่สำเร็จ{{ returnedOrder.failureReason ? ` — ${returnedOrder.failureReason}` : "" }}
              <div class="small mt-1">ยังไม่มีการเรียกเก็บเงิน เลือกแพ็กใหม่เพื่อลองอีกครั้ง</div>
            </div>
            <div v-else>
              <i class="fas fa-clock mr-1"></i>
              ยังไม่ได้รับการยืนยันจากผู้ให้บริการ
              <div class="small mt-1">
                ถ้าคุณจ่ายเงินไปแล้ว สิทธิ์จะเปิดให้อัตโนมัติเมื่อได้รับการยืนยัน
                ลองรีเฟรชหน้านี้อีกครั้งในอีกสักครู่
              </div>
            </div>
          </b-alert>
        </template>
      </b-card>
    </div>

    <!-- เลือกช่องทางจ่ายเงิน -->
    <b-modal
      ref="payMethodModal"
      title="เลือกวิธีชำระเงิน"
      ok-title="ไปหน้าชำระเงิน"
      cancel-title="ยกเลิก"
      :ok-disabled="isSubmitting"
      @ok.prevent="startCheckout"
    >
      <p v-if="selectedPlan" class="mb-3">
        {{ selectedPlan.name }} · {{ billingCycle === "YEARLY" ? "รายปี" : "รายเดือน" }} ·
        <strong>{{ formatMoney(priceOf(selectedPlan)) }} บาท</strong>
      </p>

      <b-form-group>
        <b-form-radio v-model="paymentMethod" value="CARD" class="mb-2">
          <strong>บัตรเครดิต/เดบิต</strong>
          <div class="small text-muted">ต่ออายุอัตโนมัติทุกรอบ ยกเลิกได้ตลอดเวลา</div>
        </b-form-radio>
        <b-form-radio v-model="paymentMethod" value="PROMPTPAY">
          <strong>พร้อมเพย์</strong>
          <div class="small text-muted">
            จ่ายครั้งเดียว ต่ออายุอัตโนมัติไม่ได้ ต้องกลับมาจ่ายเองก่อนหมดอายุ
          </div>
        </b-form-radio>
      </b-form-group>
    </b-modal>

    <Dialog ref="Dialog" />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import moment from "moment";
import Loading from "@/components/Loading";
import Dialog from "@/components/modal/Dialog";
import { FEATURE } from "@/constants/entitlements";

const GIGABYTE = 1024 * 1024 * 1024;

export default {
  name: "BillingPlans",
  components: { Loading, Dialog },
  data() {
    return {
      isLoading: true,
      isSubmitting: false,
      billingCycle: "MONTHLY",
      paymentMethod: "CARD",
      plans: [],
      currentPlanCode: "FREE",
      currentEndDate: null,
      selectedPlan: null,
      canPurchase: true,
      errorMessage: "",
      returnedOrder: null,
      isPolling: false,
      pollTimer: null,
      pollsLeft: 0,
    };
  },
  computed: {
    isFree() {
      return this.currentPlanCode === "FREE";
    },
    returnVariant() {
      if (this.isPolling || !this.returnedOrder) return "info";
      if (this.returnedOrder.status === "COMPLETED") return "success";
      if (this.returnedOrder.status === "FAILED") return "danger";
      return "warning";
    },
    currentPlanName() {
      const found = this.plans.find((p) => p.code === this.currentPlanCode);
      return found ? found.name : "ฟรี";
    },
  },
  created() {
    this.fetchPlans();
    this.resumeReturnedOrder();
  },
  beforeDestroy() {
    // ไม่ปล่อย timer ค้างหลังออกจากหน้า — จะยิง API ต่อไปเรื่อย ๆ โดยไม่มีใครดูผล
    if (this.pollTimer) clearTimeout(this.pollTimer);
  },
  methods: {
    ...mapActions("moduleBilling", ["createCheckout", "fetchOrderStatus"]),

    formatMoney(num) {
      return Number(num || 0).toLocaleString("th-TH");
    },
    formatDate(date) {
      return date ? moment(date).locale("th").format("D MMM YY") : "";
    },
    priceOf(plan) {
      return this.billingCycle === "YEARLY" ? plan.priceYearly : plan.priceMonthly;
    },

    /**
     * สร้างบรรทัดสรุปจาก entitlements ที่ BE ส่งมาทั้งก้อน แทนที่จะ hardcode รายการไว้ที่นี่
     * — เพิ่มสินค้าใหม่ที่ BE แล้วหน้านี้จะโชว์ให้เองโดยไม่ต้องแก้ ยกเว้นคีย์ที่ต้องมีหน่วย
     * หรือคำอธิบายเฉพาะ ซึ่งแปลไว้ข้างล่าง
     */
    featureLines(plan) {
      const e = plan.entitlements || {};
      const lines = [];
      const storage = e[FEATURE.STORAGE];
      if (storage) {
        lines.push(
          storage.value === null
            ? "พื้นที่เก็บไฟล์ไม่จำกัด"
            : `พื้นที่เก็บไฟล์ ${Math.round(storage.value / GIGABYTE)} GB`
        );
      }
      const chatbot = e[FEATURE.DENTCARE_CHATBOT];
      if (chatbot && chatbot.value) lines.push(`แชทบอทตอบคนไข้ ${this.formatMoney(chatbot.value)} ครั้ง/เดือน`);
      const mcp = e[FEATURE.MCP_TOOL_CALL];
      if (mcp && mcp.value) lines.push(`ผู้ช่วย AI ${this.formatMoney(mcp.value)} ครั้ง/เดือน (รวมทั้งคลินิก)`);
      if (e[FEATURE.DENTCARE_REMINDER] && e[FEATURE.DENTCARE_REMINDER].value) {
        lines.push("แจ้งเตือนนัดหมายผ่านแอป DentCare");
      }
      if (e[FEATURE.DOCUMENT_GENERATOR] && e[FEATURE.DOCUMENT_GENERATOR].value) {
        lines.push("ใบยินยอม/ใบส่งตัว และเซ็นเก็บเข้าแฟ้ม");
      }
      if (e[FEATURE.AD_FREE] && e[FEATURE.AD_FREE].value) lines.push("ไม่มีโฆษณาในแอป");
      return lines;
    },

    async fetchPlans() {
      this.isLoading = true;
      try {
        const { clinicUrl, branchUrl } = this.$route.params;
        const data = await this.$store.dispatch("moduleBilling/fetchPlans", { clinicUrl, branchUrl });
        this.plans = data.plans || [];
        this.currentPlanCode = data.currentPlanCode || "FREE";
        this.currentEndDate = data.currentEndDate || null;
      } catch (err) {
        this.errorMessage = "ไม่สามารถโหลดรายการแพ็กเกจได้ กรุณาลองใหม่อีกครั้ง";
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * ผู้ใช้กลับมาจากหน้าจ่ายเงิน — ถามสถานะจนกว่าจะรู้ผล
     *
     * **การกลับมาถึงหน้านี้ไม่ได้แปลว่าจ่ายสำเร็จ** ผู้ใช้กลับมาก่อน webhook เข้าเป็นเรื่องปกติ
     * และ URL ปลอมได้ ทุกอย่างที่แสดงมาจากคำตอบของ server ไม่ใช่จาก query string
     *
     * เลข order เอาจาก query ก่อน (2C2P พากลับมาพร้อมพารามิเตอร์) แล้วค่อยถอยไปดู
     * sessionStorage เผื่อผู้ให้บริการตัด query ทิ้ง
     */
    resumeReturnedOrder() {
      let orderNumber = this.$route.query.order;
      if (!orderNumber) {
        try {
          orderNumber = sessionStorage.getItem("billingPendingOrder");
        } catch (e) {
          orderNumber = null;
        }
      }
      if (!orderNumber) return;

      // จำกัดจำนวนครั้ง ไม่วนไม่สิ้นสุด — ถ้าเกินแล้วยังไม่รู้ผล บอกให้ผู้ใช้กลับมาดูทีหลัง
      // ดีกว่าปล่อยให้หน้าค้างหมุนตลอดกาลหรือถล่ม API
      this.pollsLeft = 10;
      this.isPolling = true;
      this.pollOrder(orderNumber);
    },

    async pollOrder(orderNumber) {
      try {
        const { clinicUrl, branchUrl } = this.$route.params;
        const data = await this.fetchOrderStatus({ clinicUrl, branchUrl, orderNumber });
        this.returnedOrder = data;

        if (data.settled) {
          this.isPolling = false;
          try {
            sessionStorage.removeItem("billingPendingOrder");
          } catch (e) {
            // ไม่เป็นไร ค่าที่ค้างอยู่จะถูกทับตอนซื้อครั้งถัดไป
          }
          // สิทธิ์เพิ่งเปลี่ยน โหลดแพ็กใหม่เพื่อให้หน้าจอตรงกับความจริง
          if (data.status === "COMPLETED") this.fetchPlans();
          return;
        }
      } catch (err) {
        // 404 = เลขที่ค้างอยู่ไม่ใช่ของสาขานี้ (เปลี่ยนสาขาแล้ว) เลิกถามและล้างทิ้ง
        if (err?.response?.status === 404) {
          this.isPolling = false;
          this.returnedOrder = null;
          try {
            sessionStorage.removeItem("billingPendingOrder");
          } catch (e) {
            // เงียบไว้ ไม่มีอะไรให้ทำต่อ
          }
          return;
        }
      }

      this.pollsLeft -= 1;
      if (this.pollsLeft <= 0) {
        this.isPolling = false;
        return;
      }
      this.pollTimer = setTimeout(() => this.pollOrder(orderNumber), 3000);
    },

    choose(plan) {
      this.selectedPlan = plan;
      this.errorMessage = "";
      this.$refs.payMethodModal.show();
    },

    async startCheckout() {
      if (!this.selectedPlan || this.isSubmitting) return;
      this.isSubmitting = true;
      this.errorMessage = "";
      try {
        const { clinicUrl, branchUrl } = this.$route.params;
        const result = await this.createCheckout({
          clinicUrl,
          branchUrl,
          planCode: this.selectedPlan.code,
          billingCycle: this.billingCycle,
          paymentMethod: this.paymentMethod,
        });

        // จำเลขคำสั่งซื้อไว้ก่อนออกจากหน้า — ผู้ใช้กลับมาแล้วต้องถามสถานะต่อได้
        // ห้ามถือว่าซื้อสำเร็จเพราะเดินทางกลับมาถึง สิทธิ์เปิดโดย webhook ฝั่ง server เท่านั้น
        try {
          sessionStorage.setItem("billingPendingOrder", result.orderNumber);
        } catch (e) {
          // โหมดส่วนตัวหรือปิด storage — ไม่ใช่เหตุให้หยุดการซื้อ แค่กลับมาแล้วต้องรีเฟรชเอง
        }

        window.location.href = result.webPaymentUrl;
      } catch (err) {
        const status = err?.response?.status;
        if (status === 403) {
          this.canPurchase = false;
          this.errorMessage = "บัญชีของคุณไม่มีสิทธิ์สั่งซื้อแพ็กเกจให้สาขานี้";
        } else {
          this.errorMessage =
            err?.response?.data?.message || "ไม่สามารถเริ่มการชำระเงินได้ กรุณาลองใหม่อีกครั้ง";
        }
        this.$refs.payMethodModal.hide();
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.plan-card {
  transition: box-shadow 0.15s ease;
}
.plan-card:hover {
  box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.1);
}
.plan-price {
  font-size: 1.6rem;
  font-weight: 700;
}
.plan-features li {
  margin-bottom: 0.35rem;
}
</style>
